import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { Dialog } from '@/components/ui/dialog';
import { Grid, XStack, YStack } from '@/components/ui/layout/layout';

import {
  TeamRoleDisplayLabel,
  type TeamUsageMember,
  type TeamUsageResponse,
} from '../../team.types';

import { MessagesBarChart } from './MessagesBarChart';
import { activityPercentile, fillDays } from './usage-format';
import { UsageStatTile } from './UsageStatTile';

export const TeamUsageMemberDrawer = ({
  member,
  members,
  window,
  onClose,
}: {
  member: TeamUsageMember | null;
  members: TeamUsageMember[];
  window: TeamUsageResponse['window'];
  onClose: () => void;
}) => {
  const reducedMotion = useReducedMotion();
  return (
    <DialogPrimitive.Root
      open={member !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AnimatePresence>
        {member && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.2 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              asChild
              forceMount
              aria-describedby={undefined}
            >
              <motion.aside
                className="fixed inset-y-0 right-0 z-50 flex w-[27rem] max-w-[92vw] flex-col border-l border-border-strong bg-background shadow-xl outline-none"
                initial={{ x: '100%' }}
                animate={{
                  x: 0,
                  transition: {
                    duration: reducedMotion ? 0 : 0.3,
                    ease: [0.22, 1, 0.36, 1],
                  },
                }}
                exit={{
                  x: '100%',
                  transition: {
                    duration: reducedMotion ? 0 : 0.2,
                    ease: [0.4, 0, 1, 1],
                  },
                }}
              >
                <DrawerHeader member={member} />
                <DrawerBody member={member} members={members} window={window} />
              </motion.aside>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
};

const DrawerHeader = ({ member }: { member: TeamUsageMember }) => (
  <XStack className="items-start justify-between gap-3 border-b border-border px-5 py-4">
    <YStack className="min-w-0 gap-1">
      <DialogPrimitive.Title asChild>
        <span className="text-base font-medium text-foreground">
          {member.name ?? member.email}
        </span>
      </DialogPrimitive.Title>
      <span className="text-xs text-foreground-muted">{member.email}</span>
      <span className="text-xs text-foreground-muted">
        {TeamRoleDisplayLabel[member.role]}
        {member.joined_at &&
          ` · Joined ${format(parseISO(member.joined_at), 'MMM yyyy')}`}
        {!member.is_active && ' · no activity this period'}
      </span>
    </YStack>
    <Dialog.Close />
  </XStack>
);

const DrawerBody = ({
  member,
  members,
  window,
}: {
  member: TeamUsageMember;
  members: TeamUsageMember[];
  window: TeamUsageResponse['window'];
}) => {
  const stats: Array<[string, number]> = [
    ['Messages', member.messages],
    ['Integrations', member.integration_actions],
    ['Meetings', member.meetings],
    ['Routine runs', member.routine_runs],
  ];
  return (
    <YStack className="flex-1 gap-5 overflow-y-auto px-5 py-4">
      <YStack className="gap-2.5">
        <DrawerSectionTitle>Usage this period</DrawerSectionTitle>
        <Grid className="grid-cols-2 gap-2.5">
          {stats.map(([label, value]) => (
            <UsageStatTile
              key={label}
              value={value}
              label={label}
              className="bg-surface"
            />
          ))}
        </Grid>
      </YStack>

      {member.is_active && (
        <YStack className="gap-2.5">
          <DrawerSectionTitle>Messages over time</DrawerSectionTitle>
          <MessagesBarChart
            series={fillDays(member.messages_by_day, window.start, window.days)}
            height={120}
          />
        </YStack>
      )}

      <div className="rounded-lg border border-dashed border-border-strong bg-background-muted px-3.5 py-3 text-sm text-foreground">
        {member.is_active ? (
          <>
            More active than <b>{activityPercentile(member, members)}%</b> of
            the team this period.
          </>
        ) : (
          'No activity in the selected range. Consider checking in or reassigning the seat.'
        )}
      </div>
    </YStack>
  );
};

const DrawerSectionTitle = ({ children }: { children: string }) => (
  <span className="text-[11px] font-semibold text-foreground-muted">
    {children}
  </span>
);
